import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = `${window.location.origin}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function getStoredToken() {
  return localStorage.getItem('zipai_access_token');
}

function getStoredRefreshToken() {
  return localStorage.getItem('zipai_refresh_token');
}

function storeTokens(access, refresh) {
  if (access) localStorage.setItem('zipai_access_token', access);
  if (refresh) localStorage.setItem('zipai_refresh_token', refresh);
}

function clearTokens() {
  localStorage.removeItem('zipai_access_token');
  localStorage.removeItem('zipai_refresh_token');
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const setUserRef = useRef(setUser);

  const checkAuth = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setUserRef.current(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserRef.current(data);
    } catch {
      // Try refresh
      const refreshToken = getStoredRefreshToken();
      if (refreshToken) {
        try {
          const { data: refreshData } = await axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken });
          if (refreshData.access_token) {
            storeTokens(refreshData.access_token, null);
            const { data } = await axios.get(`${API}/auth/me`, {
              headers: { Authorization: `Bearer ${refreshData.access_token}` }
            });
            setUserRef.current(data);
            setLoading(false);
            return;
          }
        } catch { /* refresh failed */ }
      }
      clearTokens();
      setUserRef.current(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const actions = useMemo(() => ({
    login: async (email, password) => {
      try {
        const { data } = await axios.post(`${API}/auth/login`, { email, password });
        if (data.access_token) {
          storeTokens(data.access_token, data.refresh_token);
        }
        setUserRef.current(data);
        return { success: true };
      } catch (e) {
        return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
      }
    },
    register: async (name, email, password, phone_number) => {
      try {
        const { data } = await axios.post(`${API}/auth/register`, { name, email, password, phone_number });
        return { success: true, message: data.message };
      } catch (e) {
        return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
      }
    },
    logout: async () => {
      try {
        const token = getStoredToken();
        await axios.post(`${API}/auth/logout`, {}, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
      } catch { /* logout failure is non-critical */ }
      clearTokens();
      setUserRef.current(false);
    },
    forgotPassword: async (email) => {
      try {
        const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
        return { success: true, message: data.message };
      } catch (e) {
        return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
      }
    },
    resetPassword: async (token, password) => {
      try {
        const { data } = await axios.post(`${API}/auth/reset-password`, { token, password });
        return { success: true, message: data.message };
      } catch (e) {
        return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
      }
    },
  }), []);

  const value = useMemo(() => ({
    user, loading, checkAuth, ...actions
  }), [user, loading, checkAuth, actions]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
